import { useState, useEffect } from 'react';
import { 
  Boxes, 
  Plus, 
  Search, 
  Package,
  AlertTriangle,
  Edit2,
  Trash2,
  ArrowUpDown,
  DollarSign,
  Beaker,
  Puzzle,
  Paintbrush,
  Box,
  Cpu,
  Paperclip,
  MoreHorizontal,
  Wrench
} from 'lucide-react';
import { Button } from '@/components/common/Button';
import { useToast } from '@/hooks/useToast';
import client from '@/api/client';
import { EditSupplyModal } from '@/components/supplies/EditSupplyModal';
import { EntityLink } from '@/components/detail/EntityLink';
import {
  CRAFT_TYPES_BY_CLASS,
  SUPPLY_CLASSES,
  supplyClassFromType,
} from '@/components/supplies/supplyFormConstants';
import { normalizeUploadedImageUrl } from '@/utils/imageUrls';
import type { Supply, SupplyClass, SupplyType } from '@/types/api';

const supplyTypeLabels: Partial<Record<SupplyType, string>> = {
  BLANK_SUBSTRATE: 'Blank Substrate',
  BLANK_APPAREL: 'Blank Apparel',
  HARDWARE: 'Hardware',
  PACKAGING: 'Packaging',
  ELECTRONIC: 'Electronic',
  RAW_MATERIAL: 'Raw Material',
  MAINTENANCE_PART: 'Maintenance',
  ACCESSORY: 'Accessory',
  OTHER: 'Other',
  ...Object.fromEntries(
    Object.values(CRAFT_TYPES_BY_CLASS)
      .flat()
      .map((option) => [option.value, option.label])
  ) as Partial<Record<SupplyType, string>>,
};

const supplyClassIcons: Record<SupplyClass, React.ElementType> = {
  CUSTOMIZABLE_BLANK: Box,
  CONSUMABLE: Beaker,
  HARDWARE_COMPONENT: Puzzle,
  PACKAGING: Box,
  BLANK_APPAREL: Paintbrush,
  ELECTRONIC: Cpu,
  RAW_MATERIAL: Package,
  MAINTENANCE_PART: Wrench,
  ACCESSORY: Paperclip,
  OTHER: MoreHorizontal,
};

const supplyClassColors: Record<SupplyClass, string> = {
  CUSTOMIZABLE_BLANK: 'bg-teal-500/10 text-teal-500 dark:text-teal-400 border-teal-500/20',
  CONSUMABLE: 'bg-orange-500/10 text-orange-500 dark:text-orange-400 border-orange-500/20',
  HARDWARE_COMPONENT: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  PACKAGING: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  BLANK_APPAREL: 'bg-emerald-500/10 text-emerald-500 dark:text-emerald-400 border-emerald-500/20',
  ELECTRONIC: 'bg-cyan-600/10 text-cyan-600 dark:text-cyan-400 border-cyan-600/20',
  RAW_MATERIAL: 'bg-amber-700/10 text-amber-600 border-amber-700/20',
  MAINTENANCE_PART: 'bg-gray-600/10 text-gray-600 dark:text-gray-400 border-gray-600/20',
  ACCESSORY: 'bg-indigo-500/10 text-indigo-500 dark:text-indigo-400 border-indigo-500/20',
  OTHER: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
};

export function Supplies() {
  const [supplies, setSupplies] = useState<Supply[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('ALL');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSupply, setEditingSupply] = useState<Supply | null>(null);
  const { error: showError, success: showSuccess } = useToast();

  useEffect(() => {
    fetchSupplies();
  }, []);

  const fetchSupplies = async () => {
    try {
      setLoading(true);
      const response = await client.get('/supplies/all');
      setSupplies(response.data);
    } catch (error) {
      showError('Failed to load supplies');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this supply?')) return;
    try {
      await client.delete(`/supplies/${id}`);
      showSuccess && showSuccess('Supply deleted successfully');
      fetchSupplies();
    } catch (error) {
      showError('Failed to delete supply');
    }
  };

  const filteredSupplies = supplies.filter(supply => {
    const matchesSearch = supply.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         supply.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         supply.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         supply.supplyTypeDisplay?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'ALL' || supply.supplyType === selectedType;
    const matchesStock = !showLowStockOnly || supply.isLowStock;
    return matchesSearch && matchesType && matchesStock;
  });

  const totalValue = supplies.reduce((sum, s) => sum + (s.stockValue || 0), 0);
  const lowStockCount = supplies.filter(s => s.isLowStock).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-amber-450/10 border border-amber-450/20">
            <Boxes className="w-6 h-6 text-amber-450" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-warm-cream">Supplies</h1>
            <p className="text-gray-600 dark:text-warm-sand">Manage inventory supplies and components</p>
          </div>
        </div>
        <Button variant="primary" onClick={() => { setEditingSupply(null); setIsModalOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Add Supply
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Total Supplies</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-warm-cream mt-1">{supplies.length}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-500/10">
              <Boxes className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Inventory Value</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-warm-cream mt-1">${totalValue.toFixed(2)}</p>
            </div>
            <div className="p-3 rounded-lg bg-green-500/10">
              <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Low Stock</p>
              <p className={`text-2xl font-bold mt-1 ${lowStockCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-warm-cream'}`}>
                {lowStockCount}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${lowStockCount > 0 ? 'bg-red-500/10' : 'bg-gray-100 dark:bg-charcoal-700'}`}>
              <AlertTriangle className={`w-6 h-6 ${lowStockCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-warm-sand'}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600 dark:text-warm-sand" />
          <input
            type="text"
            placeholder="Search supplies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-charcoal-800 border border-gray-200 dark:border-charcoal-700 rounded-lg text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-charcoal-500 focus:ring-2 focus:ring-amber-450/50 focus:border-amber-450"
          />
        </div>
        
        <div className="flex gap-3">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-4 py-2.5 bg-white dark:bg-charcoal-800 border border-gray-200 dark:border-charcoal-700 rounded-lg text-gray-900 dark:text-warm-cream focus:ring-2 focus:ring-amber-450/50 focus:border-amber-450"
          >
            <option value="ALL">All Types</option>
            {SUPPLY_CLASSES.map((supplyClass) => (
              <optgroup
                key={supplyClass.value}
                label={`${supplyClass.emoji} ${supplyClass.label}`}
              >
                {CRAFT_TYPES_BY_CLASS[supplyClass.value].map((typeOption) => (
                  <option key={typeOption.value} value={typeOption.value}>
                    {typeOption.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          <label className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-charcoal-800 border border-gray-200 dark:border-charcoal-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 transition-colors">
            <input
              type="checkbox"
              checked={showLowStockOnly}
              onChange={(e) => setShowLowStockOnly(e.target.checked)}
              className="rounded border-gray-300 dark:border-charcoal-600 text-amber-450 focus:ring-amber-450"
            />
            <span className="text-sm text-gray-900 dark:text-warm-cream">Low Stock Only</span>
          </label>
        </div>
      </div>

      {/* Supplies Table */}
      <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-100 dark:bg-charcoal-700/50">
            <tr>
              <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                Photo
              </th>
              <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                <button className="flex items-center gap-1 hover:text-gray-900 dark:text-warm-cream">
                  Supply
                  <ArrowUpDown className="w-3.5 h-3.5" />
                </button>
              </th>
              <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                Type
              </th>
              <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                SKU
              </th>
              <th className="text-right py-3 px-4 text-xs font-medium text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                Stock
              </th>
              <th className="text-right py-3 px-4 text-xs font-medium text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                Cost/Unit
              </th>
              <th className="text-right py-3 px-4 text-xs font-medium text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                Value
              </th>
              <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                Status
              </th>
              <th className="text-right py-3 px-4 text-xs font-medium text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-charcoal-700">
            {loading ? (
              <tr>
                <td colSpan={9} className="py-12 text-center text-gray-600 dark:text-warm-sand">
                  Loading supplies...
                </td>
              </tr>
            ) : filteredSupplies.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-12 text-center text-gray-600 dark:text-warm-sand">
                  No supplies found
                </td>
              </tr>
            ) : (
              filteredSupplies.map((supply) => {
                const supplyClass = supply.supplyClass ?? supplyClassFromType(supply.supplyType);
                const Icon = supplyClassIcons[supplyClass] || Package;
                return (
                  <tr key={supply.id} className="hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700/30">
                    <td className="py-4 px-4">
                      <div className={`w-12 h-12 rounded-lg ${supplyClassColors[supplyClass]} flex items-center justify-center overflow-hidden`}>
                        {normalizeUploadedImageUrl(supply.imageUrl) ? (
                          <img 
                            src={normalizeUploadedImageUrl(supply.imageUrl) ?? undefined}
                            alt={supply.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Icon className="w-5 h-5" />
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <EntityLink type="supply" id={supply.id} label={supply.name} />
                        <div className="text-sm text-gray-600 dark:text-warm-sand">{supply.description}</div>
                        {supply.dimensionHeight != null && supply.dimensionWidth != null && supply.dimensionDepth != null && (
                          <div className="text-xs text-gray-500 dark:text-warm-sand/60 mt-0.5 font-mono">
                            {supply.dimensionHeight}" × {supply.dimensionWidth}" × {supply.dimensionDepth}" {supply.dimensionUnit ?? 'in'}
                          </div>
                        )}
                        {supply.isBlank && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 mt-0.5">
                            Blank
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${supplyClassColors[supplyClass] ?? supplyClassColors.OTHER}`}>
                        {supplyTypeLabels[supply.supplyType] ?? supply.supplyTypeDisplay ?? supply.supplyType}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600 dark:text-warm-sand font-mono">
                      {supply.sku || '-'}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="font-medium text-gray-900 dark:text-warm-cream">
                        {supply.quantityInStock.toLocaleString()} {supply.unitOfMeasure}
                      </div>
                      {supply.isLowStock && (
                        <div className="text-xs text-red-600 dark:text-red-400 flex items-center justify-end gap-1 mt-1">
                          <AlertTriangle className="w-3 h-3" />
                          Below {supply.reorderLevel} {supply.unitOfMeasure}
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-4 text-right text-sm text-gray-900 dark:text-warm-cream">
                      ${supply.costPerUnit.toFixed(4)}
                    </td>
                    <td className="py-4 px-4 text-right text-sm text-gray-900 dark:text-warm-cream">
                      ${(supply.stockValue ?? 0).toFixed(2)}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {supply.isActive ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-500/10 text-gray-400 border border-gray-500/20">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-600 dark:text-warm-sand hover:text-gray-900 dark:text-warm-cream"
                          onClick={() => { setEditingSupply(supply); setIsModalOpen(true); }}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 dark:text-red-400 hover:text-red-300"
                          onClick={() => handleDelete(supply.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <EditSupplyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchSupplies}
        supply={editingSupply}
      />
    </div>
  );
}
